import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Star, Lock, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StuckButton } from "@/components/StuckButton";
import { generatePDF } from "@/lib/pdf";
import { displayText } from "@/lib/displayText";
import { useAuth } from "@/context/AuthContext";
import { trackWhop } from "@/lib/whop";
import { clarityEvent, getRef } from "@/lib/clarity";

const TypeBadge = ({ type }) => <span className="type-badge">{type === "physique" ? "BUSINESS PHYSIQUE" : "BUSINESS EN LIGNE"}</span>;

const Section = ({ number, title, children, testid }) => (
  <section className="result-section" data-testid={testid}>
    <div className="result-section-heading"><span className="mono">{number} /</span><h3>{title}</h3></div>
    {children}
  </section>
);

// Decoy content shown fully blurred behind the paywall (never the real answer).
const FAKE = {
  business_name: "Ton business personnalisé arrive",
  type: "online",
  pitch: "Une phrase qui présente ton business et pourquoi il est fait pour ta situation exacte.",
  skills_used: ["Une compétence", "Une autre compétence"],
  why_you: [
    "La première raison, adaptée à ton profil et à tes réponses au questionnaire.",
    "La deuxième raison qui explique pourquoi ce business te correspond vraiment.",
    "La troisième raison sur ton potentiel et ce que tu peux accomplir en un mois.",
  ],
  target_client: "La description précise et concrète des clients que tu vas servir chaque semaine.",
  pricing: "Une fourchette de prix claire et détaillée pour tes prestations.",
  startup_cost: "Le budget exact pour démarrer sereinement, adapté à ce que tu as.",
  first_month_potential: "Ton potentiel de revenus estimé dès le premier mois d'activité.",
  tools: ["Premier outil", "Deuxième outil", "Troisième outil", "Quatrième outil"],
  plan_30_days: Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    title: "Action concrète du jour à découvrir",
    tasks: ["Première tâche à réaliser aujourd'hui", "Deuxième action pour avancer vite", "Troisième étape vers tes clients"],
  })),
  first_client_script: "Le script complet, mot pour mot, pour convaincre ton tout premier client sans stresser ni improviser.",
  risks_and_solutions: [
    { risk: "Un risque possible au démarrage de ton activité", solution: "La solution concrète pour le gérer sans paniquer." },
    { risk: "Un autre obstacle courant que rencontrent les débutants", solution: "Comment le contourner facilement dès le début." },
  ],
};

export default function Result() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checks, setChecks] = useState({});
  const [accepted, setAccepted] = useState(false);
  const justPaid = params.get("paying") === "1";
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.email) setEmail((e) => e || user.email);
  }, [user]);

  const fetchResult = useCallback(async () => {
    const { data } = await api.get(`/result/${id}`);
    return data;
  }, [id]);

  useEffect(() => {
    let mounted = true;
    fetchResult().then((d) => {
      if (!mounted) return;
      setData(d);
      setChecks(d.checkboxes || {});
    }).catch(() => toast.error("Résultat introuvable"));
    return () => { mounted = false; };
  }, [fetchResult]);

  // Poll while the plan is still being finalized after payment.
  useEffect(() => {
    if (!(data && data.status === "unlocked" && !data.generated)) return;
    const t = setInterval(async () => {
      try {
        const d = await fetchResult();
        if (d.generated) { setData(d); setChecks(d.checkboxes || {}); clearInterval(t); }
      } catch (e) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [data, fetchResult]);

  // After returning from Whop checkout, poll until the signed webhook unlocks the result.
  useEffect(() => {
    if (!justPaid) return;
    if (data && data.status === "unlocked") return;
    const t = setInterval(async () => {
      try {
        const d = await fetchResult();
        setData(d);
        setChecks(d.checkboxes || {});
        if (d.status === "unlocked") clearInterval(t);
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [justPaid, data, fetchResult]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Regeneration: paid owners can ask for a completely different business idea.
  const [regenInfo, setRegenInfo] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const loadRegen = useCallback(async () => {
    if (!user) return;
    try { const { data: r } = await api.get(`/result/${id}/regen-status`); setRegenInfo(r); } catch (e) { /* ignore */ }
  }, [id, user]);
  useEffect(() => {
    if (user && data && data.status === "unlocked" && data.generated) loadRegen();
  }, [user, data, loadRegen]);

  const handleRegenerate = async (orientation) => {
    if (regenerating) return;
    setRegenerating(true);
    clarityEvent("regenerate_business");
    try {
      const { data: r } = await api.post(`/result/${id}/regenerate`, { orientation: orientation || [] });
      setRegenInfo((p) => ({ ...(p || {}), ...r, can_regen: r.unlimited || r.remaining > 0 }));
      setData((d) => ({ ...d, generated: false, business: null }));
      toast.success("Nouvelle idée, totalement différente, en cours de génération...");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de régénérer pour le moment.");
    } finally { setRegenerating(false); }
  };

  // Mark the business as "discovered" the first time the client opens it (feeds progress + delivery proof).
  const bizOpenedRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (data && data.status === "unlocked" && data.generated && data.business && !bizOpenedRef.current) {
      bizOpenedRef.current = true;
      api.post("/space/business-opened").catch(() => {});
    }
  }, [user, data]);

  // Phase the paywall in: blurred teaser first, then the offers slide up after 1.5s.
  const [showPricing, setShowPricing] = useState(false);
  const leadTracked = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (data.status === "unlocked" || justPaid) { setShowPricing(true); return; }
    if (!leadTracked.current) { leadTracked.current = true; trackWhop("lead"); clarityEvent("prix_affiches"); }
    setShowPricing(false);
    const t = setTimeout(() => setShowPricing(true), 1500);
    return () => clearTimeout(t);
  }, [data, justPaid]);

  const handleUnlock = async (planKey, consent) => {
    if (!accepted) return;
    if (!user && !emailValid) return;
    trackWhop("add_to_cart");
    clarityEvent(`clic_offre_${planKey}`);
    setSubmitting(true);
    try {
      if (email) await api.post("/email", { result_id: id, email, ref: getRef() });
      const { data: co } = await api.post("/checkout", { result_id: id, email: email || undefined, plan: planKey, ...(consent || {}) });
      if (co.mode === "whop_hosted" && co.checkout_url) { window.location.assign(co.checkout_url); return; }
      if (co.mode === "mock") navigate(co.checkout_url);
      else if (co.checkout_url) window.location.assign(co.checkout_url);
    } catch (e) {
      toast.error("Une erreur est survenue, réessaie.");
      setSubmitting(false);
    }
  };

  const toggleCheck = async (day) => {
    const newVal = !checks[day];
    setChecks((c) => ({ ...c, [day]: newVal }));
    try { await api.post(`/result/${id}/check`, { day, checked: newVal }); } catch (e) {}
  };

  const handleExpire = useCallback(() => {
    setDiscountExpired(true);
  }, []);

  const [discountExpired, setDiscountExpired] = useState(false);
  const expiredTracked = useRef(false);
  useEffect(() => {
    if (data && data.discount_expired) setDiscountExpired(true);
  }, [data]);
  useEffect(() => {
    if (discountExpired && !expiredTracked.current) {
      expiredTracked.current = true;
      clarityEvent("reduction_expiree");
    }
  }, [discountExpired]);

  if (!data) {
    return <div className="center-page" role="status" data-testid="result-loading"><p className="eyebrow">Ouverture de ton résultat...</p></div>;
  }

  if (data.status === "expired") {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container page-content fade-in" data-testid="result-expired">
          <p className="eyebrow accent">TON IDÉE A EXPIRÉ</p>
          <h1>Ta réservation de 10 minutes est <em>terminée.</em></h1>
          <p className="muted mt-6 max-w-[520px]">Ton idée n'est plus disponible. Refais le questionnaire pour en générer une nouvelle, gratuitement.</p>
          <button onClick={() => navigate("/quiz")} data-testid="expired-restart" className="btn-primary mt-8">Refaire le questionnaire <span aria-hidden="true">→</span></button>
        </main>
        <Footer />
      </div>
    );
  }

  const showReal = data.status === "unlocked" && data.generated && data.business;
  const finalizing = data.status === "unlocked" && !data.generated;
  const goal = Number(data.revenue_goal || 0).toLocaleString("fr-FR");

  return (
    <div className="min-h-screen has-stuck-fab">
      <Navbar />
      <main className="container result-layout page-content">
        {showReal ? (
          <div className="reveal-unblur">
            <RealHero b={data.business} />
            {user && regenInfo && regenInfo.owns && (
              <RegenerateBar info={regenInfo} regenerating={regenerating} onRegenerate={handleRegenerate} />
            )}
            <UnlockedContent b={data.business} checks={checks} toggleCheck={toggleCheck} />
            <ReviewPrompt resultId={id} defaultName={user?.name || ""} />
          </div>
        ) : (
          <div className="result-locked" data-testid="result-locked">
            <div className="result-blur" aria-hidden="true">
              <FakeHero />
              <UnlockedContent b={FAKE} checks={{}} toggleCheck={() => {}} fake />
            </div>

            {!showPricing && !finalizing && (
              <div className="locked-tease" data-testid="locked-tease">
                <span className="locked-lock" aria-hidden="true"><Lock size={20} /></span>
                <p className="eyebrow">TON BUSINESS</p>
                <h2 className="locked-tease-title" data-testid="locked-tease-title">Ton business personnalisé</h2>
                <p className="locked-tease-sub">Basé sur tes réponses au questionnaire</p>
              </div>
            )}

            {showPricing && (
              <div className="paywall-overlay">
                <div className="paywall-card paywall-reveal" data-testid="paywall-card">
                  {finalizing ? (
                    <Finalizing />
                  ) : (
                    <>
                      {!justPaid && data.reserved_until && <ReservationCountdown reservedUntil={data.reserved_until} onExpire={handleExpire} expired={discountExpired} />}
                      <PaywallCard goal={goal} email={email} setEmail={setEmail} emailValid={emailValid} submitting={submitting} handleUnlock={handleUnlock} justPaid={justPaid} user={user} accepted={accepted} setAccepted={setAccepted} discountExpired={discountExpired} />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      {showReal && user && <StuckButton context="mon business" />}
      <Footer />
    </div>
  );
}

function RegenerateBar({ info, regenerating, onRegenerate }) {
  const unlimited = info.unlimited;
  const remaining = unlimited ? "illimitées" : `${info.remaining} restante${info.remaining > 1 ? "s" : ""}`;
  const canRegen = unlimited || info.remaining > 0;
  const CHOICES = [
    { key: "plus_simple", label: "Plus simple" },
    { key: "sans_visage", label: "Sans montrer mon visage" },
    { key: "autre_domaine", label: "Avec un autre domaine" },
  ];
  const [sel, setSel] = useState([]);
  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  return (
    <div className="regen-bar" data-testid="regen-bar">
      <div>
        <p className="eyebrow">CE BUSINESS NE TE CORRESPOND PAS ?</p>
        <p className="regen-bar-text">Génère-en un <em>autre</em>, totalement différent. Oriente l'IA si tu veux :</p>
        <div className="regen-choices" data-testid="regen-choices">
          {CHOICES.map((c) => (
            <button key={c.key} type="button" onClick={() => toggle(c.key)}
              className={`regen-chip ${sel.includes(c.key) ? "on" : ""}`}
              aria-pressed={sel.includes(c.key)} data-testid={`regen-chip-${c.key}`}>
              {sel.includes(c.key) && <Check size={13} aria-hidden="true" />} {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="regen-bar-action">
        <button onClick={() => onRegenerate(sel)} disabled={regenerating || !canRegen} data-testid="regenerate-btn" className="btn-primary">
          <RefreshCw size={16} className={regenerating ? "spin" : ""} />
          {regenerating ? "Génération..." : "Régénérer mon business"}
        </button>
        <span className="regen-bar-count" data-testid="regen-remaining">{canRegen ? `Régénérations ${remaining}` : "Plus de régénération ce mois-ci"}</span>
      </div>
    </div>
  );
}

function RealHero({ b }) {
  return (
    <div className="result-hero" data-testid="result-hero">
      <div className="flex flex-wrap items-center justify-between gap-4"><TypeBadge type={b.type} /><span className="eyebrow" data-testid="result-status">DOSSIER COMPLET / DÉVERROUILLÉ</span></div>
      <h1 data-testid="result-business-name">{displayText(b.business_name)}</h1>
      <p data-testid="result-pitch">{displayText(b.pitch)}</p>
      <button onClick={() => generatePDF(b)} data-testid="download-pdf" className="btn-secondary mt-7">Télécharger en PDF <span aria-hidden="true">↓</span></button>
    </div>
  );
}

function FakeHero() {
  return (
    <div className="result-hero">
      <div className="flex flex-wrap items-center justify-between gap-4"><TypeBadge type={FAKE.type} /><span className="eyebrow">TON BUSINESS / APERÇU</span></div>
      <h1>{FAKE.business_name}</h1>
      <p>{FAKE.pitch}</p>
    </div>
  );
}

const PLANS = [
  { key: "depart", name: "Départ", period: "mois", price: "9,99 €", perDay: "0,33 € / jour", old: "19,99 €", normal: "19,99 €", normalPerDay: "0,67 € / jour", label: "Ton business + 2 régénérations + plan 30 jours + script du premier client + 10 messages coach IA" },
  { key: "lancement", name: "Lancement", period: "3 mois", price: "19,99 €", perDay: "0,22 € / jour", old: "39,99 €", normal: "39,99 €", normalPerDay: "0,44 € / jour", featured: true, label: "Tout le Départ + parcours 90 jours + 30 posts/mois + prospection + grille de prix + 100 messages coach IA" },
  { key: "acceleration", name: "Accélération", period: "an", price: "49,99 €", perDay: "0,14 € / jour", old: "99,99 €", normal: "99,99 €", normalPerDay: "0,27 € / jour", label: "Tout le Lancement + coach IA et régénérations illimités + plan réajusté chaque semaine + tableau de bord des revenus" },
];

function ReservationCountdown({ reservedUntil, onExpire, expired }) {
  const target = new Date(reservedUntil).getTime();
  const [left, setLeft] = useState(() => Math.max(0, Math.floor((target - Date.now()) / 1000)));
  const fired = useRef(false);
  useEffect(() => {
    if (expired) return; // already expired: static banner, no timer, no onExpire loop
    const tick = () => {
      const s = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setLeft(s);
      if (s <= 0 && !fired.current) { fired.current = true; if (onExpire) onExpire(); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservedUntil, expired]);
  if (expired) {
    return (
      <div className="reservation reservation--expired" data-testid="reservation-expired">
        <span className="reservation-label">Ta réduction a expiré</span>
      </div>
    );
  }
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="reservation" data-testid="reservation-countdown">
      <div className="reservation-glow" aria-hidden="true" />
      <div className="reservation-head">
        <span className="reservation-dot" aria-hidden="true" />
        <span className="reservation-label">Ta réduction est réservée pendant 10 minutes</span>
      </div>
      <div className="reservation-clock" data-testid="reservation-time" aria-label={`${mm}:${ss}`}>
        <span className="reservation-seg">{mm}</span>
        <span className="reservation-colon">:</span>
        <span key={ss} className="reservation-seg reservation-seg--pop">{ss}</span>
      </div>
    </div>
  );
}

function PaywallCard({ goal, email, setEmail, emailValid, submitting, handleUnlock, justPaid, user, accepted, setAccepted, discountExpired }) {
  const [selected, setSelected] = useState("lancement");
  const [waiver, setWaiver] = useState(false);
  const canPay = accepted && waiver && (user || emailValid) && !submitting;
  const sel = PLANS.find((p) => p.key === selected);
  const dispPrice = sel.price;
  const cgvText = `J'accepte les CGV et je comprends qu'il s'agit d'un abonnement ${sel.key === "lancement" ? "trimestriel renouvelé tous les 3 mois" : "mensuel renouvelé automatiquement"}.`;
  const waiverText = "Je demande l'accès immédiat au service, je renonce à mon droit de rétractation de 14 jours et j'accepte la politique de remboursement.";
  const onContinue = () => { clarityEvent(`clic_continuer_${selected}`); handleUnlock(sel.key, { cgv_accepted: accepted, cgv_text: cgvText, waiver_accepted: waiver, waiver_text: waiverText }); };
  return (
    <>
      <h2>Ton business est <em>prêt.</em></h2>
      <p className="muted text-sm" data-testid="paywall-goal">Ton plan vers ton objectif : {goal} € en 3 mois.</p>

      <div className="plan-rows" data-testid="plan-rows">
        {PLANS.map((p) => {
          const isSel = selected === p.key;
          return (
            <div key={p.key} onClick={() => setSelected(p.key)} data-testid={`plan-${p.key}`} role="button" aria-pressed={isSel} className={`plan-row ${isSel ? "selected" : ""} ${p.featured ? "featured" : ""}`}>
              {p.featured && <span className="plan-row-top-badge">LE PLUS CHOISI</span>}
              <div className="plan-row-main">
                <span className={`plan-radio ${isSel ? "on" : ""}`} aria-hidden="true">{isSel && <Check size={13} />}</span>
                <div className="plan-row-info">
                  <div className="plan-row-headline">
                    <span className="plan-row-name">{p.name}</span>
                    <span className="plan-discount-badge">-50 %</span>
                  </div>
                  <div className="plan-row-price">
                    <span className="plan-price-old">{p.old}</span>
                    <span className="plan-price-now">{p.price}</span>
                    <small>/{p.period}</small>
                  </div>
                </div>
                <span className="plan-row-perday">{p.perDay}</span>
              </div>
              <p className="plan-row-label" data-testid={`plan-label-${p.key}`}>{p.label}</p>
            </div>
          );
        })}
      </div>

      <div className="paywall-guarantee" data-testid="paywall-guarantee">
        <span className="paywall-guarantee-icon" aria-hidden="true"><ShieldCheck size={16} /></span>
        <span>Garantie 48 h : applique tes 2 premiers jours. Pas convaincu·e ? Remboursé.</span>
      </div>

      {!user && !justPaid && (
        <>
          <label className="field-label" htmlFor="paywall-email">Ton adresse email</label>
          <input id="paywall-email" type="email" autoComplete="email" data-clarity-mask="true" data-testid="paywall-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ton@email.com" className="field" />
        </>
      )}

      <label className="cgv-row" data-testid="cgv-row">
        <span className={`cgv-box ${accepted ? "on" : ""}`}>{accepted && <Check size={13} />}</span>
        <input type="checkbox" className="sr-only" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="cgv-checkbox" />
        <span>J'accepte les <a href="/cgv" className="accent">CGV</a> et je comprends qu'il s'agit d'un abonnement {sel.key === "lancement" ? "trimestriel renouvelé tous les 3 mois" : "mensuel renouvelé automatiquement"}.</span>
      </label>

      <label className="cgv-row" data-testid="waiver-row">
        <span className={`cgv-box ${waiver ? "on" : ""}`}>{waiver && <Check size={13} />}</span>
        <input type="checkbox" className="sr-only" checked={waiver} onChange={(e) => setWaiver(e.target.checked)} data-testid="waiver-checkbox" />
        <span>Je demande l'accès immédiat au service, je renonce à mon droit de rétractation de 14 jours et j'accepte la <a href="/remboursement" className="accent">politique de remboursement</a>.</span>
      </label>

      {justPaid ? (
        <p className="muted text-sm mt-4" role="status" data-testid="payment-confirming">Confirmation du paiement...</p>
      ) : (
        <>
          <button onClick={onContinue} disabled={!canPay} data-testid="unlock-cta" className="btn-primary w-full mt-4">
            {submitting ? "Ouverture du paiement..." : `Continuer — ${dispPrice}/${sel.period}`}{!submitting && <span aria-hidden="true">→</span>}
          </button>
          <p className="subscription-note" data-testid="subscription-note">Prix bloqué tant que tu restes abonné·e · Résiliable en 1 clic · Apple Pay / Google Pay</p>
        </>
      )}
    </>
  );
}

function Finalizing() {
  return (
    <div data-testid="finalizing-card">
      <p className="eyebrow accent">PAIEMENT CONFIRMÉ</p>
      <h2>Finalisation de ton <em>plan...</em></h2>
      <div className="load40-bar" style={{ margin: "24px 0" }}><div className="load40-bar-fill" style={{ width: "99%" }} /></div>
      <p className="muted text-sm">On met la dernière main à ton business. Ça arrive dans quelques secondes.</p>
    </div>
  );
}

function ReviewPrompt({ resultId, defaultName }) {
  const storeKey = `review_done_${resultId}`;
  const [done, setDone] = useState(() => Boolean(typeof window !== "undefined" && localStorage.getItem(storeKey)));
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState(defaultName || "");
  const [busy, setBusy] = useState(false);

  if (done) {
    return (
      <div className="review-prompt" data-testid="review-thanks">
        <p className="eyebrow accent">MERCI</p>
        <p className="muted text-sm mt-2">Ton avis a bien été envoyé. Il s'affichera après validation.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!rating) { toast.error("Choisis une note en étoiles."); return; }
    if (!name.trim()) { toast.error("Ajoute ton prénom."); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { result_id: resultId, rating, comment: comment.trim(), name: name.trim() });
      localStorage.setItem(storeKey, "1");
      setDone(true);
      toast.success("Merci pour ton avis !");
    } catch (e) {
      toast.error("Une erreur est survenue, réessaie.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="review-prompt" data-testid="review-prompt">
      <p className="eyebrow">TON AVIS COMPTE</p>
      <h4 className="mt-2">Content de ton business ?</h4>
      <p className="muted text-sm mt-1">Note ton expérience en quelques secondes (optionnel).</p>
      <div className="review-stars-input" role="radiogroup" aria-label="Note de 1 à 5 étoiles">
        {Array.from({ length: 5 }).map((_, i) => {
          const on = i < (hover || rating);
          return (
            <button key={i} type="button" data-testid={`review-star-${i + 1}`} aria-label={`${i + 1} étoile${i ? "s" : ""}`} aria-checked={rating === i + 1} role="radio"
              className={on ? "on" : ""} onMouseEnter={() => setHover(i + 1)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i + 1)}>
              <Star size={26} fill={on ? "currentColor" : "none"} strokeWidth={on ? 0 : 1.5} />
            </button>
          );
        })}
      </div>
      <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Un commentaire ? (optionnel)" rows={2} className="field mt-3" />
      <div className="review-prompt-row">
        <input data-testid="review-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ton prénom" className="field" />
        <button onClick={submit} disabled={busy} data-testid="review-submit" className="btn-primary btn-small">{busy ? "Envoi..." : "Envoyer mon avis"}</button>
      </div>
    </div>
  );
}

function UnlockedContent({ b, checks, toggleCheck, fake }) {
  const weeks = [1, 2, 3, 4];
  return (
    <div>
      {(b.skills_used && b.skills_used.length > 0) && (
        <Section number="01" title="Construit sur tes compétences" testid={fake ? undefined : "section-skills"}>
          <div className="flex flex-wrap gap-2">{b.skills_used.map((s, i) => <span key={i} className="tool-label">{displayText(s)}</span>)}</div>
        </Section>
      )}
      <Section number="02" title="Pourquoi toi" testid={fake ? undefined : "section-why"}><ul className="space-y-3">{(b.why_you || []).map((r, i) => <li key={i} className="flex gap-3"><Check size={16} className="accent shrink-0 mt-1" aria-hidden="true" /><span>{displayText(r)}</span></li>)}</ul></Section>
      <Section number="03" title="Client cible" testid={fake ? undefined : "section-target"}><p>{displayText(b.target_client)}</p></Section>
      <div className="result-numbers">
        <div className="result-number" data-testid={fake ? undefined : "business-pricing"}><p className="eyebrow">PRIX</p><p>{displayText(b.pricing)}</p></div>
        <div className="result-number" data-testid={fake ? undefined : "business-startup-cost"}><p className="eyebrow">COÛT DE DÉPART</p><p>{displayText(b.startup_cost)}</p></div>
        <div className="result-number" data-testid={fake ? undefined : "business-potential"}><p className="eyebrow">POTENTIEL 1ER MOIS</p><p className="accent">{displayText(b.first_month_potential)}</p></div>
      </div>
      <Section number="03" title="Outils à utiliser" testid={fake ? undefined : "section-tools"}><div className="flex flex-wrap gap-2">{(b.tools || []).map((t, i) => <span key={i} className="tool-label">{displayText(t)}</span>)}</div></Section>
      <section data-testid={fake ? undefined : "section-plan"}>
        <div className="plan-header"><h3>Ton plan de <em>30 jours.</em></h3><span className="mono muted text-xs" data-testid={fake ? undefined : "plan-completion"}>{Object.values(checks).filter(Boolean).length} / 30 JOURS COMPLÉTÉS</span></div>
        {weeks.map((w) => {
          const days = (b.plan_30_days || []).filter((d) => d.day > (w - 1) * 7.5 && d.day <= w * 7.5);
          return (
            <div key={w} className="plan-week">
              <h4 className="plan-week-label mono">SEMAINE 0{w}</h4>
              {days.map((d) => <div key={d.day} className="plan-day" data-testid={fake ? undefined : `plan-day-${d.day}`}>
                <span className="plan-day-number mono">JOUR {String(d.day).padStart(2, "0")}</span>
                <div className={checks[d.day] ? "opacity-50 line-through" : ""}><h4>{displayText(d.title)}</h4><ul>{(d.tasks || []).map((t, i) => <li key={i}>— {displayText(t)}</li>)}</ul></div>
                <button onClick={() => toggleCheck(d.day)} data-testid={fake ? undefined : `day-check-${d.day}`} className="day-checkbox" role="checkbox" aria-checked={Boolean(checks[d.day])} aria-label={`Marquer le jour ${d.day} comme ${checks[d.day] ? "à faire" : "terminé"}`}>{checks[d.day] && <Check size={16} />}</button>
              </div>)}
            </div>
          );
        })}
      </section>
      <Section number="04" title="Script pour ton 1er client" testid={fake ? undefined : "section-script"}><p className="whitespace-pre-line">« {displayText(b.first_client_script)} »</p></Section>
      <Section number="05" title="Risques & solutions" testid={fake ? undefined : "section-risks"}><div className="space-y-6">{(b.risks_and_solutions || []).map((r, i) => <div key={i}><p className="font-medium">{displayText(r.risk)}</p><p className="mt-1">→ {displayText(r.solution)}</p></div>)}</div></Section>
    </div>
  );
}
